import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import { IconChartBar, IconDownload, IconColumns } from '@tabler/icons-react'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { groupByOptions } from '@/utils/constants'

import WidgetContext from './widget.context'
import Select from '../forms/Select'
import { parseToCsv } from '../helpers/parseToCsv'
import { exportCsv, exportCsvFromUrl } from '../helpers/exportToCsv'

const WidgetControls = ({
  allowMinimization,
  label,
  subtitle,
  subtitlePercent,
  showGroupBy,
  showPercent,
  showViewToggle,
  showCsvDownload,
  customControls,
  alternateUrlCsvClickHandler,
  customDisplayName,
  showIgnoreWeekends,
  displayBackground,
}) => {
  const {
    isMinimized,
    isPercent,
    groupByValue,
    isTableView,
    ignoreWeekends,
    csvData,
    handleMinimizationToggle,
    handlePercentToggle,
    handleChangeGroupBy,
    handleChangeView,
    handleChangeIgnoreWeekends,
  } = useContext(WidgetContext)
  const { filters } = useSelector((state) => state.analytics)

  let actualSubtitle = subtitle
  if (showPercent) {
    actualSubtitle = isPercent ? subtitlePercent : subtitle
  }

  const downloadData = (response) => {
    // A streamed export arrives as a Blob the server already formatted as
    // CSV, so it is served straight from its object URL. Parsing it into rows
    // and rebuilding the CSV here would only re-do the server's work, and the
    // `data:` URI exportCsv builds cannot hold a large export anyway.
    if (response?.blobUrl) {
      exportCsvFromUrl(
        response.blobUrl,
        response.filters || filters,
        response.fileName || label,
        customDisplayName
      )
      return
    }

    if (!isEmpty(response.data)) {
      const csvString = parseToCsv(response)

      exportCsv(
        csvString,
        response.filters || filters,
        response.fileName || label,
        customDisplayName
      )
    }
  }

  return (
    <header
      data-testid="widget-controls"
      className={classNames('widget__header', { blank: !displayBackground })}
    >
      <div>
        <div className="widget__title">
          <h2>{label}</h2>
          {allowMinimization && (
            <Button basic xs onClick={handleMinimizationToggle}>
              {isMinimized ? 'Show' : 'Hide'}
            </Button>
          )}
        </div>
        {subtitle && !isMinimized && (
          <p data-testid="subtitle" className="widget-subtitle">
            {actualSubtitle}
          </p>
        )}
      </div>
      {!isMinimized && (
        <div className="widget__controls">
          {showIgnoreWeekends && (
            <div data-testid="ignoreWeekends">
              <Checkbox
                data-testid="ignore-weekends-toggle"
                label="Ignore Weekends"
                name="ignoreWeekends"
                checked={ignoreWeekends}
                onChange={handleChangeIgnoreWeekends}
              />
            </div>
          )}
          {showPercent && (
            <div data-testid="percent">
              <Checkbox
                data-testid="percent-toggle"
                label="Show Percentage"
                name="isPercent"
                checked={isPercent}
                onChange={handlePercentToggle}
              />
            </div>
          )}
          {showGroupBy && (
            <div data-testid="group-by-select">
              <Select
                options={groupByOptions}
                value={groupByValue}
                onChange={handleChangeGroupBy}
                isClearable={false}
              />
            </div>
          )}
          {showCsvDownload && (
            <div data-testid="csv-download">
              <Button
                square
                icon={<IconDownload />}
                tooltip="Download CSV"
                aria-label="Download CSV"
                data-testid="csv-download-button"
                onClick={async () => {
                  if (alternateUrlCsvClickHandler) {
                    const data = await alternateUrlCsvClickHandler()
                    downloadData(data)
                  } else {
                    downloadData(csvData)
                  }
                }}
              />
            </div>
          )}
          {showViewToggle && (
            <Button
              square
              icon={
                isTableView ? (
                  <IconChartBar data-testid="chart" />
                ) : (
                  <IconColumns data-testid="table" />
                )
              }
              tooltip={isTableView ? 'Show chart' : 'Show table'}
              aria-label={isTableView ? 'Show chart' : 'Show table'}
              onClick={handleChangeView}
              data-testid="view-toggle"
            />
          )}
          {customControls}
        </div>
      )}
    </header>
  )
}

export default WidgetControls
